import { Stack } from '@chakra-ui/react';
import React from 'react';

import { Badge, Typography } from 'shared';

import { WidgetCardWrapper } from 'features/chat/components/ChatMessages/ChatMessage/Widgets/CardWrapper';
import { Widget } from 'features/chat/config/types/Message';

import {
  getStatusBadgeTokens,
  getWidgetMeta,
  getWidgetPayload,
  getWidgetSubtitle,
  getWidgetTitle,
  getWidgetType,
  resolveStatusText,
  getFirstString,
  WidgetMeta,
} from './widgetMeta';

type SprintWidgetProps = {
  widget: Widget;
  meta?: WidgetMeta;
};

export const SprintWidget = ({ widget, meta }: SprintWidgetProps) => {
  const computedMeta = meta ?? getWidgetMeta(widget);
  const title =
    getWidgetTitle(widget, computedMeta) ?? computedMeta.entityLabel;
  const subtitle = getWidgetSubtitle(widget, computedMeta);
  const statusTokens = getStatusBadgeTokens(computedMeta.statusVariant);
  const statusText = resolveStatusText(widget, computedMeta);
  const details = buildSprintDetails(widget, subtitle);

  return (
    <WidgetCardWrapper>
      <Stack gap="12px" alignItems="flex-start">
        <Badge colorPalette="lightGray">{computedMeta.entityLabel}</Badge>

        <Stack gap="6px" width="100%">
          <Typography type="meta" lineHeight="normal">
            {title}
          </Typography>

          {details.map((detail) => (
            <Typography
              key={detail}
              fontSize="14px"
              fontWeight="400"
              color="gray"
              lineHeight="normal"
            >
              {detail}
            </Typography>
          ))}
        </Stack>

        <Badge
          bg={statusTokens.bg}
          color={statusTokens.color}
          borderRadius="12px"
        >
          {statusText}
        </Badge>
      </Stack>
    </WidgetCardWrapper>
  );
};

const buildSprintDetails = (widget: Widget, subtitle?: string): string[] => {
  const details = new Set<string>();

  if (subtitle) {
    details.add(subtitle);
  }

  const payload = getWidgetPayload(widget);
  const widgetType = getWidgetType(widget);

  if (
    widgetType === 'activeSprintRenamed' ||
    widgetType === 'draftSprintRenamed'
  ) {
    const newName = getFirstString(payload, ['new_name', 'newName']);

    if (newName) {
      details.add(`Renamed to ${newName}`);
    }
  }

  if (widgetType === 'activeSprintFinished') {
    const completedAt = getFirstString(payload, [
      'completed_at',
      'completedAt',
    ]);

    if (completedAt) {
      details.add(`Completed ${completedAt}`);
    }
  }

  if (widgetType === 'activeSprintRestarted') {
    const restartedAt = getFirstString(payload, [
      'restarted_at',
      'restartedAt',
    ]);

    if (restartedAt) {
      details.add(`Restarted ${restartedAt}`);
    }
  }

  if (widgetType === 'draftSprintStarted') {
    const startedAt = getFirstString(payload, ['started_at', 'startedAt']);

    if (startedAt) {
      details.add(`Started ${startedAt}`);
    }
  }

  return Array.from(details);
};
